<template>
  <div class="hotel-detail-page">
    <header class="detail-header">
      <button
        class="back-btn"
        type="button"
        :aria-label="$t('utils.cancel')"
        @click="$router.back()"
      >
        <van-icon name="arrow-left" />
      </button>
      <button
        class="header-tab"
        :class="{ active: activeTab === 'hotel' }"
        type="button"
        @click="activeTab = 'hotel'"
      >
        {{ $t('hotelDetail.hotel') }}
      </button>
      <button
        class="header-tab"
        :class="{ active: activeTab === 'review' }"
        type="button"
        @click="scrollToReview"
      >
        {{ $t('hotelDetail.review') }}
      </button>
    </header>

    <main class="detail-main">
      <section class="hero-section">
        <img
          :src="mainImage"
          alt=""
          @error="mainImageError = true"
        >
      </section>

      <section class="price-section">
        <div class="price-line">
          <span>$</span>
          <strong>{{ formatPrice(pros.price) }}</strong>
        </div>
        <h1>{{ pros.title || '--' }}</h1>
      </section>

      <section class="shop-card">
        <button
          class="shop-link"
          type="button"
          @click="goShopPage"
        >
          <img
            class="shop-logo"
            :src="shopImage"
            alt=""
            @error="shopImageError = true"
          >
          <span>{{ shop.name || $t('hotelDetail.noShop') }}</span>
          <van-icon name="arrow" />
        </button>
        <div
          v-if="gallery.length"
          class="gallery-row"
        >
          <article
            v-for="item in gallery"
            :key="item.id"
            class="gallery-card"
            role="button"
            tabindex="0"
            @click="goProsDetail(item)"
            @keyup.enter="goProsDetail(item)"
          >
            <img
              :src="galleryImage(item)"
              alt=""
              @error="onGalleryImageError"
            >
            <p>{{ item.title || pros.title || '' }}</p>
          </article>
        </div>
      </section>

      <section
        ref="reviewPanel"
        class="review-card"
      >
        <h2>{{ $t('hotelDetail.review') }}</h2>
        <div
          v-for="item in reviewRows"
          :key="item.key"
          class="score-row"
        >
          <div class="score-head">
            <span>{{ item.label }}</span>
            <strong>{{ item.score }}</strong>
          </div>
          <div class="score-track">
            <i :style="{ width: item.percent + '%' }" />
          </div>
        </div>
      </section>
    </main>

    <!--    <div
      v-if="app && app.kefu_link"
      class="kefu"
      @click.stop="kefuTo"
    >
      <div class="kefu_img">
        <van-icon name="service-o" />
      </div>
    </div> -->

    <footer class="action-bar">
      <button
        type="button"
        class="bar-tool"
        @click="goKefuPage"
      >
        <van-icon name="chat-o" />
        <span>{{ $t('hotelDetail.service') }}</span>
      </button>
      <button
        type="button"
        class="bar-tool"
        @click="goShopPage"
      >
        <van-icon name="shop-o" />
        <span>{{ $t('hotelDetail.store') }}</span>
      </button>
      <button
        type="button"
        class="bar-tool"
        :class="{ active: isFav }"
        :disabled="favLoading || !pros.id"
        @click="toggleFavorite"
      >
        <van-icon :name="isFav ? 'star' : 'star-o'" />
        <span>{{ $t('hotelDetail.favorite') }}</span>
      </button>
      <button
        type="button"
        class="order-btn"
        @click="showOrderTip"
      >
        {{ $t('hotelDetail.order') }}
      </button>
    </footer>
  </div>
</template>

<script>
import Vue from 'vue';
import Fetch from '../../utils/fetch';
import { Icon, Toast } from 'vant';

Vue.use(Icon).use(Toast);

export default {
  name: 'Hotelit',
  data() {
    return {
      app: {},
      activeTab: 'hotel',
      pros: {},
      shop: {},
      gallery: [],
      loading: false,
      mainImageError: false,
      shopImageError: false,
      hotelFallback: require('../images/shop2/thumbnail.png'),
      shopFallback: require('../images/shop2/thumbnail.png'),
      isFav: false,
      favLoading: false,
    };
  },
  computed: {
    mainImage() {
      if (this.mainImageError) {
        return this.hotelFallback;
      }
      return this.buildImageUrl(this.pros.img, this.hotelFallback);
    },
    shopImage() {
      if (this.shopImageError) {
        return this.shopFallback;
      }
      return this.buildImageUrl(this.shop.img, this.shopFallback);
    },
    reviewRows() {
      var labels = [
        { key: 'cleanliness', label: this.$t('hotelDetail.cleanliness') },
        { key: 'facility', label: this.$t('hotelDetail.facility') },
        { key: 'location', label: this.$t('hotelDetail.location') },
        { key: 'comfort', label: this.$t('hotelDetail.comfort') },
        { key: 'value', label: this.$t('hotelDetail.value') },
      ];
      return labels.map((item, index) => {
        var score = this.scoreFor(index);
        return {
          key: item.key,
          label: item.label,
          score: score.toFixed(2),
          percent: Math.max(0, Math.min(100, score * 10)),
        };
      });
    },
  },
  watch: {
    '$route.query.id'(newId, oldId) {
      if (String(newId || '') === String(oldId || '')) {
        return;
      }
      this.activeTab = 'hotel';
      this.loadDetail();
      window.scrollTo(0, 0);
    },
  },
  created() {
    if (this.$parent && this.$parent.footer) {
      this.$parent.footer('walfare', false);
    }
  },
  mounted() {
    this.start();
  },
  methods: {
    start() {
      Fetch('/index/webconfig').then((r) => {
        this.app = r.data || {};
      });
      this.loadDetail();
    },
    loadDetail() {
      if (this.loading) {
        return;
      }
      var id = Number(this.$route.query.id || 0);
      if (!id) {
        Toast(this.$t('hotelDetail.empty'));
        this.$router.replace('/hotellist');
        return;
      }
      this.loading = true;
      Fetch('/index/pros_detail', { id: id }, null, true).then((r) => {
        var data = r.data || {};
        this.pros = data.pros || {};
        this.shop = data.shop || {};
        this.gallery = Array.isArray(data.gallery) ? data.gallery : [];
        this.mainImageError = false;
        this.shopImageError = false;
        this.loadFavoriteStatus();
      }).catch(() => {
        Toast(this.$t('hotelDetail.empty'));
      }).finally(() => {
        this.loading = false;
      });
    },
    loadFavoriteStatus() {
      if (!this.pros || !this.pros.id) {
        this.isFav = false;
        return;
      }
      Fetch('/user/fav_status', {
        type: 'pros',
        id: this.pros.id,
      }).then((r) => {
        var data = r.data || {};
        this.isFav = Number(data.is_fav || 0) === 1;
      }).catch(() => {
        this.isFav = false;
      });
    },
    toggleFavorite() {
      if (!this.pros || !this.pros.id || this.favLoading) {
        return;
      }
      this.favLoading = true;
      Fetch('/user/fav_toggle', {
        type: 'pros',
        id: this.pros.id,
      }, null, true).then((r) => {
        var data = r.data || {};
        this.isFav = Number(data.is_fav || 0) === 1;
        Toast(this.responseMessage(r));
      }).finally(() => {
        this.favLoading = false;
      });
    },
    responseMessage(response) {
      var info = response && response.info;
      var lang = this.$i18n.locale || 'en_us';
      if (info && typeof info === 'object') {
        return info[lang] || info.en_us || info.zh_cn || 'Success';
      }
      return info || 'Success';
    },
    scrollToReview() {
      this.activeTab = 'review';
      this.$nextTick(() => {
        if (this.$refs.reviewPanel && this.$refs.reviewPanel.scrollIntoView) {
          this.$refs.reviewPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    goShopPage() {
      if (!this.shop || !this.shop.id) {
        Toast(this.$t('hotelDetail.noShop'));
        return;
      }
      this.$router.push({
        path: '/hotel',
        query: {
          sid: this.shop.id,
        },
      });
    },
    goProsDetail(item) {
      var id = Number(item && item.id ? item.id : 0);
      if (!id) {
        return;
      }
      if (Number(this.$route.query.id || 0) === id) {
        window.scrollTo(0, 0);
        return;
      }
      this.$router.push({
        path: '/hotelit',
        query: {
          id: id,
        },
      });
    },
    showOrderTip() {
      Toast(this.$t('hotelDetail.orderUnavailable'));
    },
    kefuTo() {
      if (this.app && this.app.kefu_link) {
        window.location.href = this.app.kefu_link;
      }
    },
    goKefuPage() {
      this.$router.push('/kefu');
    },
    buildImageUrl(path, fallback) {
      if (!path) {
        return fallback;
      }
      if (/^(https?:)?\/\//.test(path) || String(path).indexOf('data:') === 0) {
        return path;
      }
      if (this.app && this.app.domain) {
        return this.app.domain.replace(/\/$/, '') + '/' + String(path).replace(/^\//, '');
      }
      return path;
    },
    galleryImage(item) {
      return this.buildImageUrl(item && item.img, this.hotelFallback);
    },
    onGalleryImageError(event) {
      if (event && event.target && event.target.src !== this.hotelFallback) {
        event.target.src = this.hotelFallback;
      }
    },
    formatPrice(value) {
      var number = Number(value);
      if (!isFinite(number)) {
        number = 0;
      }
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    scoreFor(index) {
      var seed = Number(this.pros.id || 1);
      var raw = (seed * (index + 7) + index * 13) % 58;
      return 7.32 + raw / 100;
    },
  },
};
</script>

<style lang="less" scoped>
.hotel-detail-page {
  min-height: 100vh;
  padding-bottom: 78px;
  background: #eaf2fb;
  color: #06152b;
}

.detail-header {
  position: sticky;
  top: 0;
  z-index: 12;
  display: grid;
  grid-template-columns: 64px 1fr 1fr;
  height: 50px;
  background: #f4f5fa;
  border-bottom: 1px solid #e8ebf0;
}

.back-btn,
.header-tab {
  border: 0;
  background: transparent;
  color: #06152b;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.back-btn .van-icon {
  font-size: 22px;
}

.header-tab {
  position: relative;
  font-size: 14px;
  line-height: 50px;
  text-align: center;
}

.header-tab.active::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 52px;
  height: 3px;
  border-radius: 3px;
  transform: translateX(-50%);
  background: #ff8b1a;
}

.detail-main {
  min-height: calc(100vh - 136px);
}

.hero-section {
  height: 270px;
  overflow: hidden;
  background: #dfe5ee;
}

.hero-section img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.price-section {
  padding: 14px 14px 10px;
  background: #fff;
}

.price-line {
  display: flex;
  align-items: baseline;
  color: #f20d18;
  font-weight: 700;
  line-height: 24px;
}

.price-line span {
  margin-right: 8px;
  font-size: 14px;
}

.price-line strong {
  font-size: 14px;
}

.price-section h1 {
  margin: 4px 0 0;
  overflow: hidden;
  color: #06152b;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shop-card,
.review-card {
  margin: 12px 12px 0;
  border-radius: 10px;
  background: #fff;
}

.shop-card {
  padding: 14px 12px 12px;
}

.shop-link {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) 20px;
  width: 100%;
  align-items: center;
  column-gap: 12px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #06152b;
  text-align: left;
}

.shop-logo {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  background: #eef2f7;
}

.shop-link span {
  overflow: hidden;
  font-size: 14px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shop-link .van-icon {
  font-size: 18px;
  color: #06152b;
}

.gallery-row {
  display: grid;
  grid-auto-columns: 28%;
  grid-auto-flow: column;
  column-gap: 10px;
  margin-top: 16px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.gallery-row::-webkit-scrollbar {
  display: none;
}

.gallery-card {
  min-width: 0;
  overflow: hidden;
  border-radius: 6px;
  background: #f3f5f8;
}

.gallery-card img {
  display: block;
  width: 100%;
  height: 74px;
  object-fit: cover;
}

.gallery-card p {
  height: 30px;
  margin: 4px 4px 6px;
  overflow: hidden;
  color: #344055;
  font-size: 10px;
  line-height: 15px;
  text-align: center;
}

.review-card {
  padding: 26px 28px 22px;
}

.review-card h2 {
  margin: 0 0 26px;
  color: #06152b;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
}

.score-row + .score-row {
  margin-top: 13px;
}

.score-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
  color: #26364d;
  font-size: 14px;
  line-height: 18px;
}

.score-head strong {
  color: #087bff;
  font-size: 14px;
  font-style: italic;
}

.score-track {
  height: 5px;
  overflow: hidden;
  border-radius: 5px;
  background: #edf0f4;
}

.score-track i {
  display: block;
  height: 100%;
  border-radius: 5px;
  background: #087bff;
}

.kefu {
  position: fixed;
  right: 15px;
  bottom: 94px;
  z-index: 80;
  width: 62px;
  height: 62px;
}

.kefu_img {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  border: 4px solid #fff;
  border-radius: 50%;
  background: linear-gradient(180deg, #1b7dff 0%, #0d80ff 64%, #fb1426 65%, #fb1426 100%);
  box-shadow: 0 6px 14px rgba(30, 70, 130, 0.25);
  color: #fff;
}

.kefu_img .van-icon {
  font-size: 31px;
}

.action-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 70;
  display: grid;
  grid-template-columns: 54px 54px 54px minmax(0, 1fr);
  align-items: center;
  width: 100%;
  max-width: 500px;
  height: 68px;
  margin: 0 auto;
  padding: 8px 12px;
  box-sizing: border-box;
  background: #fff;
  box-shadow: 0 -2px 10px rgba(20, 35, 60, 0.08);
}

.bar-tool {
  display: flex;
  height: 52px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  background: transparent;
  color: #26364d;
  font-size: 10px;
  line-height: 14px;
}

.bar-tool .van-icon {
  margin-bottom: 4px;
  font-size: 22px;
}

.bar-tool.active {
  color: #ff8a1a;
}

.order-btn {
  height: 46px;
  border: 0;
  border-radius: 24px;
  background: linear-gradient(90deg, #ffd01e 0%, #ff8a1a 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}
</style>
